/**
 * jQuery UI datepicker — position it against the field, and dismiss it on scroll.
 *
 * WHY: Gravity Forms date fields use jQuery UI's datepicker, which appends a single
 * `#ui-datepicker-div` to <body> and positions it from the input's DOCUMENT offset. Every
 * date field on this site lives inside a modal whose panel is its own scrolling container
 * (`.fr-modal__panel { overflow-y: auto }`), so that offset does not describe where the
 * field actually is on screen: the calendar landed at the top of the viewport, overlapping
 * the very field it belongs to, and its month header sat behind the admin bar. Measured:
 * field at y 24–76, calendar at y 0–231.
 *
 * The calendar is `position: fixed`, so viewport coordinates map 1:1 —
 * getBoundingClientRect() on the input gives an exact answer regardless of how many
 * scrolling ancestors sit between it and the document. We re-place it after jQuery UI has
 * shown it (rAF, so we run after its own inline top/left), flipping above the field when
 * there isn't room below and clamping to the viewport either way.
 *
 * Also hides the calendar when the modal panel scrolls: a fixed-position calendar does not
 * travel with the field, so it would otherwise hang in mid-air over the form.
 *
 * The z-index fix that makes the calendar visible above a modal at all lives in
 * overrides/_gravity-forms.scss (jQuery UI ships an inline z-index: 2).
 *
 * @param {*} _s The vanilla-jq helper (window._s).
 */
( function ( _s ) {
	const CALENDAR = '#ui-datepicker-div';
	// Breathing room between the field and the calendar, and from the viewport edges.
	const GAP = 6;
	const EDGE = 8;

	/**
	 * Is the calendar currently on screen?
	 *
	 * @param {HTMLElement} cal The `#ui-datepicker-div` element.
	 * @return {boolean} True when it is displayed.
	 */
	const isOpen = cal => !! cal && window.getComputedStyle( cal ).display !== 'none';

	/**
	 * Place the calendar directly under (or over) its input, in viewport coordinates.
	 *
	 * @param {HTMLElement} input The date input the calendar belongs to.
	 * @return {void}
	 */
	const place = input => {
		const cal = document.querySelector( CALENDAR );

		if ( ! isOpen( cal ) ) {
			return;
		}

		const field = input.getBoundingClientRect();
		const height = cal.offsetHeight;
		const width = cal.offsetWidth;
		const roomBelow = window.innerHeight - field.bottom;

		// Prefer below. Flip above only when below doesn't fit AND above does — otherwise
		// staying below keeps the calendar clear of the field it's anchored to.
		const above = roomBelow < height + GAP && field.top > height + GAP;
		const top = above ? field.top - height - GAP : field.bottom + GAP;

		let left = field.left;
		left = Math.min( left, window.innerWidth - width - EDGE );
		left = Math.max( EDGE, left );

		cal.style.position = 'fixed';
		cal.style.top = Math.max( EDGE, top ) + 'px';
		cal.style.left = left + 'px';
	};

	/**
	 * Close whichever datepicker is open.
	 *
	 * @return {void}
	 */
	const hide = () => {
		const cal = document.querySelector( CALENDAR );

		if ( ! isOpen( cal ) || ! window.jQuery ) {
			return;
		}

		window.jQuery( 'input.hasDatepicker' ).datepicker( 'hide' );
	};

	_s( document ).ready( () => {
		// jQuery UI writes its own inline top/left when it shows the calendar, so ours has
		// to land afterwards — hence rAF rather than handling the event synchronously.
		[ 'focusin', 'click' ].forEach( type => {
			document.addEventListener( type, event => {
				const input = event.target;

				if ( ! input || typeof input.matches !== 'function' ) {
					return;
				}

				if ( ! input.matches( 'input.hasDatepicker' ) ) {
					return;
				}

				window.requestAnimationFrame( () => place( input ) );
			} );
		} );

		// Scroll events don't bubble, so listen in the capture phase to catch the modal
		// panel's own scrolling as well as the window's.
		document.addEventListener( 'scroll', hide, true );
	} );
} )( window._s );
